import { useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button, DataTable, Pagination, LinkCell, Modal, TextInput, Select, RadioGroup, useToast, useTableSort, type Column,
} from '@datahub/design-system'
import { get, post } from '../../lib/api'
import { ListPage } from '../AdminShell'
import { StatusTag } from '../../components/StatusTag'
import { ExternalLink } from '../../components/ExternalLink'
import { StearsFundPicker, type CoreFundPrefill, type FundKind } from './StearsFundPicker'
import { taxonomies } from '@datahub/shared'

interface FundRow {
  id: string
  displayName: string
  entityType: FundKind
  status: string
  investorType: string[]
  investorStatus: string | null
  hqCountry: string | null
  hqRegion: string | null
  baseCurrency: string | null
  assetsUnderManagement: number | null
  totalCommitted: number | null
  regionsOfInvestment: string[]
  sectorPreferences: string[]
  cadence: string | null
  owner: { displayName: string | null; email: string } | null
  contacts: { id: string; fullName: string; email: string; status: string }[]
  currentCycle: { periodLabel: string; status: string; deadlineAt: string | null; completionPct: number } | null
}

const KIND_LABEL: Record<FundKind, string> = { gp: 'Fund manager (GP)', lp: 'Limited partner (LP)' }

/** A taxonomy slug rendered as the name a person reads. */
function label(slug: string | null | undefined, taxonomy: string): string | null {
  if (!slug) return null
  return (taxonomies[taxonomy] ?? []).find((t) => t.slug === slug)?.name ?? slug
}

function labels(slugs: string[], taxonomy: string): string {
  const names = slugs.map((s) => label(s, taxonomy)).filter(Boolean)
  return names.length > 0 ? names.join(', ') : '-'
}

/** Capital figures read in millions; the exact number lives on the fund's own page. */
function millions(value: number | null, currency: string | null): string {
  if (value === null) return '-'
  const unit = value >= 1_000_000_000 ? 'bn' : 'm'
  const scaled = value / (unit === 'bn' ? 1_000_000_000 : 1_000_000)
  const figure = scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)
  return `${currency ? `${currency} ` : ''}${figure}${unit}`
}

/**
 * The Funds book: fund managers (GPs) and limited partners (LPs), listed together with a
 * filter for either. They are onboarded, linked, chased and read exactly as companies
 * are — what differs is the form they fill in and the columns worth showing here.
 */
export function Funds() {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [creating, setCreating] = useState(false)
  const [kind, setKind] = useState<FundKind>('gp')
  const [form, setForm] = useState({ displayName: '', legalCompanyName: '', contactName: '', contactEmail: '' })
  const [prefill, setPrefill] = useState<CoreFundPrefill | null>(null)
  const { sortKey, sortDir, onSort } = useTableSort('displayName')

  const q = params.get('q') ?? ''
  const kindFilter = params.get('kind') ?? ''
  const status = params.get('status') ?? ''

  const { data, isLoading } = useQuery({
    queryKey: ['funds', q, kindFilter, status, page, pageSize, sortKey, sortDir],
    queryFn: () =>
      get<{ total: number; items: FundRow[] }>(
        `/admin/funds?${new URLSearchParams({
          ...(q ? { q } : {}), ...(kindFilter ? { kind: kindFilter } : {}), ...(status ? { status } : {}),
          page: String(page), pageSize: String(pageSize),
          ...(sortKey && sortDir ? { sort: sortKey, dir: sortDir } : {}),
        })}`,
      ),
  })

  const closeCreate = () => {
    setCreating(false)
    setForm({ displayName: '', legalCompanyName: '', contactName: '', contactEmail: '' })
    setPrefill(null)
    setKind('gp')
  }

  const create = useMutation({
    mutationFn: () =>
      post<{ company: { id: string } }>('/admin/funds', {
        kind,
        // The API re-reads the profile from Stears itself, so what lands on the record is
        // what Core actually says. The two name fields are sent as they appear, since the
        // admin may have edited them.
        ...(prefill ? { coreSlug: prefill.slug } : {}),
        displayName: form.displayName.trim(),
        legalCompanyName: form.legalCompanyName.trim(),
        contact: { fullName: form.contactName.trim(), email: form.contactEmail.trim() },
      }),
    onSuccess: (res) => {
      toast.success(`${kind === 'gp' ? 'Fund manager' : 'Limited partner'} created as a draft. Set a cadence next.`)
      closeCreate()
      void queryClient.invalidateQueries({ queryKey: ['funds'] })
      navigate(`/admin/funds/${res.company.id}`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
    setPage(1)
  }

  const columns: Column<FundRow>[] = [
    { key: 'displayName', header: 'Fund', width: 260, sticky: true, sortable: true,
      render: (f) => <LinkCell onClick={() => navigate(`/admin/funds/${f.id}`)}>{f.displayName}</LinkCell> },
    { key: 'entityType', header: 'Kind', width: 90, sortable: true, render: (f) => (f.entityType === 'gp' ? 'GP' : 'LP') },
    { key: 'status', header: 'Status', width: 120, render: (f) => <StatusTag status={f.status} /> },
    { key: 'investorType', header: 'Type', width: 200,
      render: (f) => labels(f.investorType, f.entityType === 'gp' ? 'gp_investor_types' : 'lp_types') },
    { key: 'assetsUnderManagement', header: 'AUM', width: 110, align: 'right', sortable: true,
      render: (f) => millions(f.assetsUnderManagement, f.baseCurrency) },
    { key: 'totalCommitted', header: 'Committed', width: 110, align: 'right',
      render: (f) => millions(f.totalCommitted, f.baseCurrency) },
    { key: 'hqCountry', header: 'HQ', width: 140, render: (f) => label(f.hqCountry, 'countries') ?? '-' },
    { key: 'regions', header: 'Regions', width: 200, render: (f) => labels(f.regionsOfInvestment, 'regions') },
    { key: 'cadence', header: 'Cadence', width: 110, render: (f) => f.cadence ?? '-' },
    { key: 'cycle', header: 'Current cycle', width: 170,
      render: (f) => (f.currentCycle ? <StatusTag status={f.currentCycle.status} suffix={f.currentCycle.periodLabel} /> : '-') },
    { key: 'completion', header: 'Completion', width: 110, align: 'right',
      render: (f) => (f.currentCycle ? `${f.currentCycle.completionPct}%` : '-') },
    { key: 'contacts', header: 'Contacts', width: 240,
      render: (f) => (f.contacts.length === 0 ? '-' : f.contacts.map((c) => `${c.fullName} <${c.email}>`).join(', ')) },
    { key: 'owner', header: 'Owner', width: 180, render: (f) => f.owner?.displayName ?? f.owner?.email ?? '-' },
  ]

  return (
    <ListPage
      title="Funds"
      actions={<Button variant="filled-grey" size="medium" onClick={() => setCreating(true)}>New fund</Button>}
    >
      <div className="flex items-center flex-shrink-0 bg-background-main" style={{ height: 54, padding: '0 20px', gap: 12, borderBottom: '0.5px solid #4B5563' }}>
        <TextInput
          size="small" value={q} placeholder="Search funds or contact emails" className="w-[320px]"
          onChange={(v) => setParam('q', v)}
        />
        <div style={{ width: 200 }}>
          <Select
            size="small"
            ariaLabel="Filter by fund kind"
            options={[
              { value: '', label: 'GPs and LPs' },
              { value: 'gp', label: 'Fund managers (GP)' },
              { value: 'lp', label: 'Limited partners (LP)' },
            ]}
            value={kindFilter}
            onChange={(v) => setParam('kind', v)}
            placeholder="GPs and LPs"
          />
        </div>
        <div style={{ width: 180 }}>
          <Select
            size="small"
            ariaLabel="Filter by status"
            options={[{ value: '', label: 'All statuses' }, ...['draft', 'active', 'paused', 'archived'].map((s) => ({ value: s, label: s }))]}
            value={status}
            onChange={(v) => setParam('status', v)}
            placeholder="All statuses"
          />
        </div>
      </div>

      <div className="flex-1" style={{ overflow: 'hidden' }}>
        {isLoading ? (
          <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: 20 }}>Loading…</p>
        ) : (
          <DataTable
            columns={columns} rows={data?.items ?? []} rowKey={(f) => f.id}
            sortKey={sortKey} sortDir={sortDir} onSort={onSort}
            caption="Funds" emptyMessage="No funds match those filters" maxHeight="100%"
          />
        )}
      </div>
      <Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1) }} />

      <Modal
        open={creating}
        onClose={closeCreate}
        title="New fund"
        primaryLabel="Create"
        primaryDisabled={!form.displayName.trim() || !form.legalCompanyName.trim() || !form.contactName.trim() || !form.contactEmail.trim()}
        primaryLoading={create.isPending}
        onPrimary={() => create.mutate()}
      >
        <div className="flex flex-col" style={{ gap: 20 }}>
          <RadioGroup
            label="Which kind?"
            direction="row"
            options={[
              { value: 'gp', label: KIND_LABEL.gp },
              { value: 'lp', label: KIND_LABEL.lp },
            ]}
            value={kind}
            onChange={(v) => { setKind(v as FundKind); setPrefill(null) }}
          />

          <StearsFundPicker
            kind={kind}
            disabled={create.isPending}
            onPick={(fund) => {
              setPrefill(fund)
              setForm((current) => ({
                ...current,
                displayName: fund.displayName,
                legalCompanyName: fund.legalName,
              }))
            }}
          />

          {prefill && <FundPrefillSummary prefill={prefill} onClear={() => setPrefill(null)} />}

          <TextInput label="Display name" size="large" value={form.displayName} onChange={(v) => setForm({ ...form, displayName: v })} />
          <TextInput label="Legal name" size="large" value={form.legalCompanyName} onChange={(v) => setForm({ ...form, legalCompanyName: v })} />
          <TextInput label="Contact name" size="large" value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} />
          <TextInput label="Contact email" size="large" inputType="email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} />
          <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
            The fund starts as a draft. This contact becomes its primary contact; set a cadence before making it active.
          </p>
        </div>
      </Modal>
    </ListPage>
  )
}

/**
 * What the Stears lookup filled in beyond the two name fields. Not editable here — the
 * modal is deliberately short — but the admin has to see what is about to be saved, and
 * be able to drop it if they picked the wrong match.
 */
function FundPrefillSummary({ prefill, onClear }: { prefill: CoreFundPrefill; onClear: () => void }) {
  const isGp = prefill.kind === 'gp'
  const rows: [string, ReactNode][] = ([
    ['Type', prefill.investorType.map((s) => label(s, isGp ? 'gp_investor_types' : 'lp_types')).filter(Boolean).join(', ') || null],
    ['Status', label(prefill.investorStatus, 'investor_statuses')],
    ['Legal structure', label(prefill.legalStructure, 'lp_legal_structures')],
    ['Ticket type', label(prefill.ticketType, 'lp_ticket_types')],
    ['Head office', prefill.hqCityCountry ?? label(prefill.hqCountry, 'countries')],
    ['HQ region', label(prefill.hqRegion, 'hq_regions')],
    ['Founded', prefill.foundingYear ? String(prefill.foundingYear) : null],
    ['AUM', millions(prefill.assetsUnderManagement, prefill.baseCurrency)],
    ['Committed', millions(prefill.totalCommitted, prefill.baseCurrency)],
    ['Regions', prefill.regionsOfInvestment.map((s) => label(s, 'regions')).filter(Boolean).join(', ') || null],
    ['Sectors', prefill.sectorPreferences.map((s) => label(s, 'investment_sectors')).filter(Boolean).join(', ') || null],
    ['Stages', prefill.investmentStages.map((s) => label(s, 'investment_stages')).filter(Boolean).join(', ') || null],
    ['Website', prefill.websiteUrl ? <ExternalLink value={prefill.websiteUrl} maxLength={34} /> : null],
  ] as [string, ReactNode][]).filter((r): r is [string, ReactNode] => Boolean(r[1]) && r[1] !== '-')

  return (
    <div style={{ border: '0.5px solid #4B5563', background: '#1A1B1E' }}>
      <div className="flex items-center justify-between" style={{ padding: '8px 12px', borderBottom: '0.5px solid #4B5563' }}>
        <span className="font-mulish text-white" style={{ fontSize: 12 }}>Pulled from Stears</span>
        <button
          type="button"
          onClick={onClear}
          className="text-text-hyperlink hover:underline font-mulish"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
        >
          Clear
        </button>
      </div>
      <div className="flex flex-col" style={{ padding: '8px 12px', gap: 4 }}>
        {rows.map(([key, value]) => (
          <div key={key} className="flex" style={{ gap: 8 }}>
            <span className="font-mulish text-text-support flex-shrink-0" style={{ fontSize: 10, width: '42%' }}>{key}</span>
            <span className="font-mulish text-white" style={{ fontSize: 10, flex: 1, wordBreak: 'break-word' }}>{value}</span>
          </div>
        ))}
        {prefill.description && (
          <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '15px', marginTop: 4 }}>
            {prefill.description.length > 260 ? `${prefill.description.slice(0, 260)}…` : prefill.description}
          </p>
        )}
      </div>
    </div>
  )
}
